import {
  Typography,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Checkbox,
  TextField,
  IconButton,
  Divider,
  Tooltip,
  Box,
} from '@material-ui/core';
import { ExpandMore, Add, Close, InfoOutlined } from '@material-ui/icons';
import { funcaoParticipanteDescriptions } from 'common/genericDescriptions';
import {
  Perfil,
  FuncaoParticipante,
  FuncaoInternaParticipante,
  TipoNotificacaoEnum,
  TipoDestinatariosEnum,
  ParticipanteFuncaoInfo,
  ParticipanteResponse,
  NotificacaoConfiguracaoRequest,
  AtualizarNotificacaoConfiguracaoRequest,
  NotificacaoDestinatarioRequest,
  RegistroStatus,
  type UsuarioModelResponse,
} from 'common/genericTypes';
import { Button, LgSpace, MdSpace, SmSpace, Dropdown } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { addAlert } from 'store/modules/snackBar';
import { Option } from 'common/genericOptions';

interface FuncaoConfig {
  id?: string;
  participanteFuncaoId: string;
  funcao: FuncaoParticipante | FuncaoInternaParticipante;
  ativo: boolean;
  destinatarios: NotificacaoDestinatarioRequest[];
  notificarUsuariosAtivo: boolean;
}

export const Notificacoes: React.FC = () => {
  const dispatch = useDispatch();
  const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil === Perfil.Leitura);
  const usuarioFuncao = useSelector((state: RootState) => state.auth.usuario.participante.funcao);
  const participanteLogadoId = useSelector((state: RootState) => state.auth.usuario.participante.id);

  const isDepositaria = usuarioFuncao === FuncaoInternaParticipante.Depositaria;

  const getParticipantesApi = useSelector((state: RootState) => state.participanteApi.getParticipantesResume);
  const getAllUsuariosApi = useSelector((state: RootState) => state.usuarioApi.getAllUsuarios);
  const getConfiguracoesApi = useSelector((state: RootState) => state.notificacaoApi.getConfiguracoes);
  const postConfiguracaoApi = useSelector((state: RootState) => state.notificacaoApi.postConfiguracao);
  const putConfiguracaoApi = useSelector((state: RootState) => state.notificacaoApi.putConfiguracao);

  const [participanteSelecionadoId, setParticipanteSelecionadoId] = useState<string>(participanteLogadoId);
  const [participantes, setParticipantes] = useState<ParticipanteResponse[]>([]);
  const [funcoes, setFuncoes] = useState<ParticipanteFuncaoInfo[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioModelResponse[]>([]);
  const [funcoesConfig, setFuncoesConfig] = useState<FuncaoConfig[]>([]);
  const [novosEmails, setNovosEmails] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [estadoInicial, setEstadoInicial] = useState<{
    funcoesConfig: FuncaoConfig[];
  } | null>(null);

  const tipoNotificacao = TipoNotificacaoEnum.EventoPrevistoPagamento;

  const carregarParticipantes = useCallback(async () => {
    try {
      const result = await getParticipantesApi(dispatch, { status: RegistroStatus.Aprovado }, true);
      if (isDepositaria) {
        setParticipantes(result);
      }
      else {
        const resultFiltrado = result.filter(p => p.id === participanteLogadoId);
        setParticipantes(resultFiltrado);
      }
    } catch (error) {
      dispatch(
        addAlert({
          message: 'Erro ao carregar participantes!',
          severity: 'error',
        })
      );
    }
  }, []);

  const carregarFuncoesParticipante = useCallback((participanteId: string, listaParticipantes: ParticipanteResponse[]) => {
    const participante = listaParticipantes.find(p => p.id === participanteId);
    if (!participante) {
      console.warn('Participante não encontrado:', participanteId);
      return;
    }

    const funcoesInfo: ParticipanteFuncaoInfo[] = participante.funcoes
      .filter(f => 
        f.funcao === FuncaoParticipante.Emissor || 
        f.funcao === FuncaoParticipante.Custodiante
      )
      .map((f) => ({
        id: f.participanteFuncaoId || `${participanteId}-${f.funcao}`,
        funcao: f.funcao,
        participanteId: participanteId,
        participanteRazaoSocial: participante.razaoSocial,
      }));

    setFuncoes(funcoesInfo);
    
    const initialConfig: FuncaoConfig[] = funcoesInfo.map(f => ({
      participanteFuncaoId: f.id,
      funcao: f.funcao,
      ativo: false,
      destinatarios: [],
      notificarUsuariosAtivo: false,
    }));
    
    setFuncoesConfig(initialConfig);
    
    setEstadoInicial({
      funcoesConfig: initialConfig,
    });
  }, []);

  const carregarUsuarios = useCallback(async () => {
    try {
      const result = await getAllUsuariosApi(dispatch, true);
      setUsuarios(result);
    } catch (error) {
      dispatch(
        addAlert({
          message: 'Erro ao carregar usuários!',
          severity: 'error',
        })
      );
    }
  }, []);

  const carregarConfiguracoes = useCallback(async (participanteId: string) => {
    try {
      const configuracoes = await getConfiguracoesApi(dispatch, participanteId);
      
      if (configuracoes && configuracoes.length > 0) {
        setFuncoesConfig(prev => {
          const atualizado = prev.map(fc => {
            const configExistente = configuracoes.find(c => c.participanteFuncaoId === fc.participanteFuncaoId);
            
            if (configExistente) {
              const destinatariosRequest: NotificacaoDestinatarioRequest[] = configExistente.destinatarios.map(d => ({
                tipoDestinatario: d.tipoDestinatario,
                destinatarioId: d.destinatarioId,
                email: d.email,
              }));

              const temUsuarios = destinatariosRequest.some(d => 
                d.tipoDestinatario === TipoDestinatariosEnum.TodosDaFuncao ||
                d.tipoDestinatario === TipoDestinatariosEnum.UsuariosSelecionados
              );

              return {
                ...fc,
                id: configExistente.id,
                ativo: configExistente.ativo,
                destinatarios: destinatariosRequest,
                notificarUsuariosAtivo: temUsuarios,
              };
            }
            
            return fc;
          });

          setEstadoInicial({
            funcoesConfig: atualizado,
          });

          return atualizado;
        });
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  }, []);

  useEffect(() => {
    carregarParticipantes();
    carregarUsuarios();
  }, []);

  useEffect(() => {
    if (participantes.length > 0 && participanteSelecionadoId) {
      carregarFuncoesParticipante(participanteSelecionadoId, participantes);
      carregarConfiguracoes(participanteSelecionadoId);
    }
  }, [participantes, participanteSelecionadoId, carregarFuncoesParticipante, carregarConfiguracoes]);


  const handleToggleFuncao = (participanteFuncaoId: string) => {
    setFuncoesConfig(prev =>
      prev.map(fc =>
        fc.participanteFuncaoId === participanteFuncaoId
          ? { ...fc, ativo: !fc.ativo }
          : fc
      )
    );
  };

  const adicionarDestinatario = (participanteFuncaoId: string, destinatario: NotificacaoDestinatarioRequest) => {
    setFuncoesConfig(prev =>
      prev.map(fc =>
        fc.participanteFuncaoId === participanteFuncaoId
          ? { ...fc, destinatarios: [...fc.destinatarios, destinatario] }
          : fc
      )
    );
  };

  const removerDestinatario = (participanteFuncaoId: string, index: number) => {
    setFuncoesConfig(prev =>
      prev.map(fc =>
        fc.participanteFuncaoId === participanteFuncaoId
          ? { ...fc, destinatarios: fc.destinatarios.filter((_, i) => i !== index) }
          : fc
      )
    );
  };

  const handleAdicionarEmail = (participanteFuncaoId: string) => {
    const novoEmail = novosEmails[participanteFuncaoId]?.trim();
    if (!novoEmail) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(novoEmail)) {
      dispatch(
        addAlert({
          message: 'E-mail inválido!',
          severity: 'warning',
        })
      );
      return;
    }

    adicionarDestinatario(participanteFuncaoId, {
      tipoDestinatario: TipoDestinatariosEnum.EmailsEspecificos,
      destinatarioId: null,
      email: novoEmail,
    });
    
    setNovosEmails(prev => ({ ...prev, [participanteFuncaoId]: '' }));
  };

  const filtrarUsuariosPorFuncao = (participanteFuncaoId: string): UsuarioModelResponse[] => {
    return usuarios.filter(usuario =>
      usuario.participanteFuncoes?.some(pf => pf.participanteFuncaoId === participanteFuncaoId)
    );
  };

  const getDestinatariosEmails = (destinatarios: NotificacaoDestinatarioRequest[]) => {
    return destinatarios.filter(d => d.tipoDestinatario === TipoDestinatariosEnum.EmailsEspecificos);
  };

  const temTodosDaFuncao = (destinatarios: NotificacaoDestinatarioRequest[]) => {
    return destinatarios.some(d => d.tipoDestinatario === TipoDestinatariosEnum.TodosDaFuncao);
  };

  const getUsuariosSelecionados = (destinatarios: NotificacaoDestinatarioRequest[]) => {
    return destinatarios
      .filter(d => d.tipoDestinatario === TipoDestinatariosEnum.UsuariosSelecionados && d.destinatarioId)
      .map(d => d.destinatarioId!);
  };

  const handleToggleNotificarUsuarios = (participanteFuncaoId: string) => {
    setFuncoesConfig(prev =>
      prev.map(fc => {
        if (fc.participanteFuncaoId === participanteFuncaoId) {
          const novoEstado = !fc.notificarUsuariosAtivo;
          if (novoEstado) {
            return { 
              ...fc,
              notificarUsuariosAtivo: true,
              destinatarios: [
                ...getDestinatariosEmails(fc.destinatarios),
                { tipoDestinatario: TipoDestinatariosEnum.TodosDaFuncao, destinatarioId: null, email: null }
              ]
            };
          } else {
            return { 
              ...fc,
              notificarUsuariosAtivo: false,
              destinatarios: getDestinatariosEmails(fc.destinatarios)
            };
          }
        }
        return fc;
      })
    );
  };

  const handleChangeTipoDestinatario = (participanteFuncaoId: string, tipo: TipoDestinatariosEnum) => {
    setFuncoesConfig(prev =>
      prev.map(fc => {
        if (fc.participanteFuncaoId === participanteFuncaoId) {
          const destsEmails = getDestinatariosEmails(fc.destinatarios);
          
          if (tipo === TipoDestinatariosEnum.TodosDaFuncao) {
            return {
              ...fc,
              destinatarios: [
                ...destsEmails,
                { tipoDestinatario: TipoDestinatariosEnum.TodosDaFuncao, destinatarioId: null, email: null }
              ]
            };
          } else {
            return { ...fc, destinatarios: destsEmails };
          }
        }
        return fc;
      })
    );
  };

  const handleToggleUsuario = (participanteFuncaoId: string, usuarioId: string) => {
    setFuncoesConfig(prev =>
      prev.map(fc => {
        if (fc.participanteFuncaoId === participanteFuncaoId) {
          const usuariosSelecionados = getUsuariosSelecionados(fc.destinatarios);
          const destsEmails = getDestinatariosEmails(fc.destinatarios);
          const todosDaFuncao = fc.destinatarios.filter(d => d.tipoDestinatario === TipoDestinatariosEnum.TodosDaFuncao);
          
          let novosUsuarios: string[];
          if (usuariosSelecionados.includes(usuarioId)) {
            novosUsuarios = usuariosSelecionados.filter(id => id !== usuarioId);
          } else {
            novosUsuarios = [...usuariosSelecionados, usuarioId];
          }

          return {
            ...fc,
            destinatarios: [
              ...destsEmails,
              ...todosDaFuncao,
              ...novosUsuarios.map(id => ({
                tipoDestinatario: TipoDestinatariosEnum.UsuariosSelecionados,
                destinatarioId: id,
                email: null
              }))
            ]
          };
        }
        return fc;
      })
    );
  };

  const handleSalvar = async () => {
    try {
      setLoading(true);

      const funcoesAtualizadas: FuncaoConfig[] = [];

      for (const funcaoConfig of funcoesConfig) {
        if (funcaoConfig.id) {
          const payloadAtualizar: AtualizarNotificacaoConfiguracaoRequest = {
            ativo: funcaoConfig.ativo,
            destinatarios: funcaoConfig.destinatarios,
          };
          
          await putConfiguracaoApi(dispatch, funcaoConfig.id, payloadAtualizar, true);
          funcoesAtualizadas.push(funcaoConfig);
        } else {
          const payloadCriar: NotificacaoConfiguracaoRequest = {
            participanteFuncaoId: funcaoConfig.participanteFuncaoId,
            tipoNotificacao,
            ativo: funcaoConfig.ativo,
            destinatarios: funcaoConfig.destinatarios,
          };
          
          const response = await postConfiguracaoApi(dispatch, participanteSelecionadoId, payloadCriar, true);
          
          funcoesAtualizadas.push({
            ...funcaoConfig,
            id: response.id,
          });
        }
      }

      setFuncoesConfig(funcoesAtualizadas);
      
      setEstadoInicial({
        funcoesConfig: [...funcoesAtualizadas],
      });

      dispatch(
        addAlert({
          message: 'Configurações salvas com sucesso!',
          severity: 'success',
        })
      );
    } catch (error) {
      dispatch(
        addAlert({
          message: 'Erro ao salvar configurações!',
          severity: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelar = () => {
    if (estadoInicial) {
      setFuncoesConfig([...estadoInicial.funcoesConfig]);
      setNovosEmails({});
    }
  };

  const participanteOptions: Option[] = participantes.map(p => ({
    value: p.id,
    description: p.razaoSocial,
  }));

  return (
    <>
      <SmSpace />
      <Typography variant="h2">Configurações de Notificação</Typography>
      <LgSpace />

      {isDepositaria && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Dropdown
                label="Participante"
                options={participanteOptions}
                value={participanteSelecionadoId}
                onChange={(value) => setParticipanteSelecionadoId(value)}
                disabled={loading}
              />
            </Grid>
          </Grid>
          <MdSpace />
        </>
      )}

      <Accordion defaultExpanded>
        <AccordionSummary 
          expandIcon={<ExpandMore />}
          style={{ backgroundColor: '#f5f5f5' }}
        >
          <Box display="flex" alignItems="center">
            <Typography variant="h6">Evento Previsto de Pagamento</Typography>
            <Box ml={1}>
              <Tooltip 
                title="Esta notificação é disponível apenas para as funções Emissor e Custodiante"
                arrow
                placement="top"
              >
                <InfoOutlined fontSize="small" style={{ color: '#666', cursor: 'help' }} />
              </Tooltip>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <div style={{ width: '100%', marginTop: '16px' }}>
            <Box mb={2} p={2} style={{ backgroundColor: '#f9f9f9', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
              <Typography variant="body2" color="textSecondary">
                Esta notificação é disparada todo dia útil com lembrete de eventos de pagamento que vencerão em 3 dias.
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                {funcoes.length === 0 && !loading && (
                  <Typography variant="body2" color="textSecondary">
                    Nenhuma função encontrada para este participante.
                  </Typography>
                )}

                {funcoes.map((funcao) => {
                  const config = funcoesConfig.find(fc => fc.participanteFuncaoId === funcao.id);
                  if (!config) return null;
                  
                  const usuariosFiltrados = filtrarUsuariosPorFuncao(funcao.id);

                  return (
                    <div key={funcao.id} style={{ marginBottom: 24 }}>
                      <Card variant="outlined">
                        <CardContent>
                          <Grid container alignItems="center" justifyContent="space-between">
                            <Grid item>
                              <Typography variant="h6">
                                {funcaoParticipanteDescriptions[funcao.funcao]}
                              </Typography>
                            </Grid>
                            <Grid item>
                              <Switch
                                checked={config.ativo}
                                onChange={() => handleToggleFuncao(funcao.id)}
                                color="primary"
                                disabled={isUsuarioLeitura}
                              />
                            </Grid>
                          </Grid>

                          {config.ativo && (
                            <>
                              <SmSpace />
                              <Divider />
                              <MdSpace />

                              <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                                    Usuários
                                  </Typography>

                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={config.notificarUsuariosAtivo}
                                        onChange={() => handleToggleNotificarUsuarios(funcao.id)}
                                        color="primary"
                                        disabled={isUsuarioLeitura}
                                      />
                                    }
                                    label="Notificar usuários"
                                  />

                                  {config.notificarUsuariosAtivo && (
                                    <>
                                      <SmSpace />
                                      <Dropdown
                                        label="Tipo de destinatário"
                                        options={[
                                          { value: TipoDestinatariosEnum.TodosDaFuncao, description: 'Todos da função' },
                                          { value: TipoDestinatariosEnum.UsuariosSelecionados, description: 'Usuários selecionados' },
                                        ]}
                                        value={temTodosDaFuncao(config.destinatarios) ? TipoDestinatariosEnum.TodosDaFuncao : TipoDestinatariosEnum.UsuariosSelecionados}
                                        onChange={(value) => handleChangeTipoDestinatario(funcao.id, value as TipoDestinatariosEnum)}
                                        disabled={isUsuarioLeitura}
                                      />

                                      {!temTodosDaFuncao(config.destinatarios) && (
                                        <>
                                          <MdSpace />
                                          {usuariosFiltrados.length === 0 ? (
                                            <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                                              Nenhum usuário disponível para esta função.
                                            </Typography>
                                          ) : (
                                            <div>
                                              {usuariosFiltrados.map((usuario) => (
                                                <div key={usuario.id}>
                                                  <FormControlLabel
                                                    control={
                                                      <Checkbox
                                                        checked={getUsuariosSelecionados(config.destinatarios).includes(usuario.id)}
                                                        onChange={() => handleToggleUsuario(funcao.id, usuario.id)}
                                                        color="primary"
                                                        disabled={isUsuarioLeitura}
                                                      />
                                                    }
                                                    label={usuario.nome}
                                                  />
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </>
                                  )}
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                                    Emails Específicos
                                  </Typography>
                                  
                                  <Grid container spacing={1} alignItems="flex-end">
                                    <Grid item xs={10}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        label="Adicionar email"
                                        placeholder="email@exemplo.com"
                                        value={novosEmails[funcao.id] || ''}
                                        onChange={(e) => setNovosEmails(prev => ({ ...prev, [funcao.id]: e.target.value }))}
                                        disabled={isUsuarioLeitura}
                                        onKeyPress={(e) => {
                                          if (e.key === 'Enter') {
                                            handleAdicionarEmail(funcao.id);
                                          }
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={2}>
                                      <IconButton
                                        color="primary"
                                        onClick={() => handleAdicionarEmail(funcao.id)}
                                        disabled={isUsuarioLeitura || !novosEmails[funcao.id]?.trim()}
                                      >
                                        <Add />
                                      </IconButton>
                                    </Grid>
                                  </Grid>

                                  <MdSpace />
                                  
                                  {getDestinatariosEmails(config.destinatarios).length > 0 && (
                                    <>
                                      {getDestinatariosEmails(config.destinatarios).map((dest, index) => (
                                        <Grid container key={index} spacing={1} alignItems="center" style={{ marginBottom: 8 }}>
                                          <Grid item xs={11}>
                                            <Typography variant="body2">{dest.email}</Typography>
                                          </Grid>
                                          <Grid item xs={1}>
                                            <IconButton
                                              size="small"
                                              onClick={() => {
                                                const emailsIndex = config.destinatarios.findIndex(d => d === dest);
                                                removerDestinatario(funcao.id, emailsIndex);
                                              }}
                                              disabled={isUsuarioLeitura}
                                            >
                                              <Close fontSize="small" />
                                            </IconButton>
                                          </Grid>
                                        </Grid>
                                      ))}
                                    </>
                                  )}
                                </Grid>
                              </Grid>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </Grid>
            </Grid>

            {!isUsuarioLeitura && (
              <>
                <LgSpace />
                <Divider />
                <MdSpace />
                <Grid container justifyContent="flex-end" spacing={2}>
                  <Grid item>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleCancelar}
                      disabled={loading}
                    >
                      Cancelar
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSalvar}
                      disabled={loading}
                    >
                      Salvar
                    </Button>
                  </Grid>
                </Grid>
              </>
            )}
          </div>
        </AccordionDetails>
      </Accordion>
      <LgSpace />
    </>
  );
};

export default Notificacoes;
